import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { RefreshCw } from 'lucide-react'
import { dashboardService, type DashboardOverview } from '@/services/dashboard'
import { supabase } from '@/services/supabase'
import { getUserDefaultRoute } from '@/utils/roleCache'
import { AIBlogCostWidget } from '@/components/analytics/AIBlogCostWidget'

export const Dashboard: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [data, setData] = useState<DashboardOverview>({
    total_tenants: 0,
    total_users: 0,
    active_projects: 0,
    monthly_revenue: null
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Redirect Marketers to their appropriate landing page
  useEffect(() => {
    const checkAccess = async () => {
      if (!user) return

      const defaultRoute = await getUserDefaultRoute(supabase, user.id)

      // If user's default route is not Dashboard, redirect them
      if (defaultRoute !== '/') {
        navigate(defaultRoute, { replace: true })
      }
    }

    checkAccess()
  }, [user, navigate])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)
      const overview = await dashboardService.getOverview()
      setData(overview)
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err)
      setError('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboardData()
  }, [])

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome to Joules Admin Platform</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchDashboardData} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div>
        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg text-destructive">
            {error}
          </div>
        )}
        
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader>
              <CardTitle>Monthly Revenue</CardTitle>
              <CardDescription>
                {/* ADM-2.1. Says why there is no number, rather than showing one
                    we invented. See DashboardOverview.monthly_revenue. */}
                {data.monthly_revenue === null
                  ? (data.monthly_revenue_unavailable_reason ??
                     'Not available yet.')
                  : 'Across all tenants'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? (
                  <span className="text-muted-foreground animate-pulse">...</span>
                ) : data.monthly_revenue === null ? (
                  <span className="text-muted-foreground">Not available</span>
                ) : (
                  `$${data.monthly_revenue.toLocaleString()}`
                )}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Active Projects</CardTitle>
              <CardDescription>In progress</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? (
                  <span className="text-muted-foreground animate-pulse">...</span>
                ) : (
                  data.active_projects.toLocaleString()
                )}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tenants</CardTitle>
              <CardDescription>Active tenants</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? (
                  <span className="text-muted-foreground animate-pulse">...</span>
                ) : (
                  data.total_tenants.toLocaleString()
                )}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Users</CardTitle>
              <CardDescription>Across all organizations</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? (
                  <span className="text-muted-foreground animate-pulse">...</span>
                ) : (
                  data.total_users.toLocaleString()
                )}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* AI Blog Cost Analytics Widget */}
        <div className="mt-8">
          <AIBlogCostWidget />
        </div>

        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle>Welcome, {user?.email}</CardTitle>
              <CardDescription>
                You are logged in as a super administrator. This dashboard provides access to all platform management features.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Use the navigation menu to manage tenants, view analytics, handle subscriptions, and configure platform settings.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}